import React from 'react';

/* ═══════════════════════════════════════════════════════════════════
   HERO ATLAS ILLUSTRATION — pure decorative visual, zero interactivity.
   A centered "Capital Projects Atlas" radial diagram:
     · Luminex diamond + ember core at the center (L0)
     · 4 concentric hierarchy rings labeled L1 / L2 / L3 / L4
     · 5-fold constellation of ember descent spokes (each with tier markers)
     · Dense dot field populating each tier ring
     · Subtle constellation threads linking selected L3 dots
     · Slow rotation on outer ring, pulse on center & spoke markers
═══════════════════════════════════════════════════════════════════ */

const CX = 250;
const CY = 230;
const R = { L0: 26, L1: 64, L2: 102, L3: 138, L4: 172 };

const polar = (angleDeg, r) => {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: CX + Math.cos(rad) * r, y: CY + Math.sin(rad) * r };
};

const SPOKE_ANGLES = [22, 94, 166, 238, 310]; // 5-fold constellation

const L1_DOTS = Array.from({ length: 8 }, (_, i) => i * 45 + 10);
const L2_DOTS = Array.from({ length: 16 }, (_, i) => i * 22.5 + 6);
const L3_DOTS = Array.from({ length: 32 }, (_, i) => i * 11.25 + 3);
const L4_DOTS = Array.from({ length: 24 }, (_, i) => i * 15 + 7);

// Constellation threads — pairs of L3 dot indices to connect with a soft curve
const CONSTELLATIONS = [[3, 11], [7, 20], [14, 26], [1, 18], [22, 29]];

export default function HeroAtlasIllustration() {
  return (
    <div
      data-testid="hero-atlas-illustration"
      style={{
        position: 'relative',
        background: 'rgba(15,19,28,0.72)',
        border: '1px solid rgba(87,144,201,0.22)',
        borderRadius: 14,
        padding: '22px 24px 26px',
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
      }}
    >
      {/* Eyebrow */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: 'rgba(243,244,246,0.6)', fontFamily: 'Inter, sans-serif' }}>
        <span>Capital Projects Atlas</span>
        <span style={{ color: '#F26B1F' }}>L0 → L4</span>
      </div>

      {/* SVG illustration */}
      <svg viewBox="0 0 500 460" width="100%" style={{ display: 'block' }} aria-hidden="true">
        <defs>
          <radialGradient id="atlasCoreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F26B1F" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#F26B1F" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#F26B1F" stopOpacity="0" />
          </radialGradient>
          <pattern id="atlasDotGrid" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.55" fill="#5790C9" opacity="0.10" />
          </pattern>
          <filter id="atlasSoftGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.6" />
          </filter>
        </defs>

        {/* Background texture */}
        <rect x="0" y="0" width="500" height="460" fill="url(#atlasDotGrid)" />

        {/* Ember center glow */}
        <circle cx={CX} cy={CY} r="150" fill="url(#atlasCoreGlow)" />

        {/* ═══ Constellation connector threads (between selected L3 dots) ═══ */}
        <g stroke="#5790C9" strokeOpacity="0.22" strokeWidth="0.6" fill="none">
          {CONSTELLATIONS.map(([a, b], i) => {
            const p1 = polar(L3_DOTS[a], R.L3);
            const p2 = polar(L3_DOTS[b], R.L3);
            const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
            return <path key={i} d={`M${p1.x},${p1.y} Q${CX},${CY} ${p2.x},${p2.y}`} />;
          })}
        </g>

        {/* ═══ Concentric hierarchy rings ═══ */}
        {[
          { r: R.L1, label: 'L1' },
          { r: R.L2, label: 'L2' },
          { r: R.L3, label: 'L3' },
          { r: R.L4, label: 'L4' },
        ].map((ring) => (
          <g key={ring.label}>
            <circle cx={CX} cy={CY} r={ring.r} fill="none" stroke="#5790C9" strokeOpacity="0.26" strokeWidth="0.9" strokeDasharray="2 3" />
            {/* Tier label placed at 12-o'clock, sitting cleanly above the ring */}
            <g transform={`translate(${CX}, ${CY - ring.r - 3})`}>
              <rect x="-12" y="-9" width="24" height="12" rx="2" fill="rgba(15,19,28,0.95)" />
              <text x="0" y="0" textAnchor="middle" fill="rgba(243,244,246,0.75)" fontSize="8.5" fontWeight="800" letterSpacing="1.2" fontFamily="Inter, sans-serif">{ring.label}</text>
            </g>
          </g>
        ))}

        {/* ═══ Radial guide spokes every 30° ═══ */}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = i * 30;
          const pa = polar(a, R.L1);
          const pb = polar(a, R.L4);
          return <line key={i} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke="#5790C9" strokeOpacity="0.08" strokeWidth="0.5" />;
        })}

        {/* ═══ Tier dot fields ═══ */}
        <g>
          {L1_DOTS.map((a, i) => {
            const p = polar(a, R.L1);
            return <circle key={`l1-${i}`} cx={p.x} cy={p.y} r="2.6" fill="#5790C9" opacity="0.9" />;
          })}
          {L2_DOTS.map((a, i) => {
            const p = polar(a, R.L2);
            return <circle key={`l2-${i}`} cx={p.x} cy={p.y} r="1.9" fill="#8FB3DE" opacity="0.8" />;
          })}
          {L3_DOTS.map((a, i) => {
            const p = polar(a, R.L3);
            return <circle key={`l3-${i}`} cx={p.x} cy={p.y} r="1.3" fill="#C2D5EE" opacity="0.7" />;
          })}
          {L4_DOTS.map((a, i) => {
            const p = polar(a, R.L4);
            return <circle key={`l4-${i}`} cx={p.x} cy={p.y} r="1.1" fill="#E6EEF9" opacity="0.55" />;
          })}
        </g>

        {/* ═══ 5-fold ember descent constellation ═══ */}
        {SPOKE_ANGLES.map((angle, si) => {
          const pL4 = polar(angle, R.L4);
          const pL3 = polar(angle, R.L3);
          const pL2 = polar(angle, R.L2);
          const pL1 = polar(angle, R.L1);
          return (
            <g key={`spoke-${si}`}>
              {/* Dashed ember spoke */}
              <line x1={pL4.x} y1={pL4.y} x2={CX} y2={CY} stroke="#F26B1F" strokeWidth="1.4" opacity="0.85" strokeDasharray="3 2.5">
                <animate attributeName="stroke-dashoffset" values="0;-11" dur={`${1.4 + si * 0.15}s`} repeatCount="indefinite" />
              </line>
              {/* Tier markers */}
              {[pL4, pL3, pL2, pL1].map((p, ti) => (
                <circle key={ti} cx={p.x} cy={p.y} r="2.8" fill="#F26B1F">
                  <animate attributeName="r" values="2.2;3.8;2.2" dur="1.9s" begin={`${si * 0.2 + ti * 0.15}s`} repeatCount="indefinite" />
                </circle>
              ))}
            </g>
          );
        })}

        {/* ═══ Center — pulse rings, diamond, ember core ═══ */}
        <circle cx={CX} cy={CY} r="20" fill="none" stroke="#F26B1F" strokeWidth="1.4" opacity="0.7" filter="url(#atlasSoftGlow)">
          <animate attributeName="r" values="16;28;16" dur="2.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0;0.7" dur="2.8s" repeatCount="indefinite" />
        </circle>
        <circle cx={CX} cy={CY} r="34" fill="none" stroke="#F26B1F" strokeWidth="1" opacity="0.35">
          <animate attributeName="r" values="24;44;24" dur="3.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0;0.4" dur="3.4s" repeatCount="indefinite" />
        </circle>

        {/* Center diamond — slow counter-rotation */}
        <g style={{ transformOrigin: `${CX}px ${CY}px`, animation: 'vd-atlas-rotate-slow 32s linear infinite' }}>
          <rect x={CX - 16} y={CY - 16} width="32" height="32" rx="3"
            fill="none" stroke="#5790C9" strokeWidth="1.6" opacity="0.85"
            transform={`rotate(45 ${CX} ${CY})`} />
          {/* Cardinal ticks outside the diamond */}
          <line x1={CX} y1={CY - 24} x2={CX} y2={CY - 28} stroke="#5790C9" strokeWidth="1.2" />
          <line x1={CX} y1={CY + 24} x2={CX} y2={CY + 28} stroke="#5790C9" strokeWidth="1.2" />
          <line x1={CX - 24} y1={CY} x2={CX - 28} y2={CY} stroke="#5790C9" strokeWidth="1.2" />
          <line x1={CX + 24} y1={CY} x2={CX + 28} y2={CY} stroke="#5790C9" strokeWidth="1.2" />
        </g>

        {/* Ember core dot */}
        <circle cx={CX} cy={CY} r="4.5" fill="#F26B1F">
          <animate attributeName="r" values="3.6;5;3.6" dur="1.8s" repeatCount="indefinite" />
        </circle>

        {/* L0 label below the core */}
        <text x={CX} y={CY + 48} textAnchor="middle" fill="rgba(243,244,246,0.75)" fontSize="10" fontWeight="800" letterSpacing="2" fontFamily="Inter, sans-serif">LUMINEX · L0</text>

        {/* ═══ Bottom stats strip ═══ */}
        <g transform="translate(0, 418)">
          <line x1="40" y1="0" x2="460" y2="0" stroke="rgba(243,244,246,0.12)" strokeWidth="1" />
          {[
            { v: '30', l: 'Verticals' },
            { v: '7,400+', l: 'Processes' },
            { v: '800+', l: 'Actors' },
            { v: 'L4', l: 'Depth' },
          ].map((s, i) => (
            <g key={i} transform={`translate(${100 + i * 100}, 22)`}>
              <text x="0" y="0" textAnchor="middle" fill="#F3F4F6" fontSize="20" fontWeight="700" fontFamily="'Source Serif 4', Georgia, serif" letterSpacing="-0.5">{s.v}</text>
              <text x="0" y="16" textAnchor="middle" fill="rgba(243,244,246,0.55)" fontSize="8.5" fontWeight="700" letterSpacing="1" fontFamily="Inter, sans-serif">{s.l.toUpperCase()}</text>
            </g>
          ))}
        </g>
      </svg>

      <style>{`
        @keyframes vd-atlas-rotate-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
